import { useEffect, useMemo, useState } from "react";
import { AdminPlanInfo } from "@/components/plan/AdminPlanInfo";
import axios from "axios";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Eye, EyeOff, Trash2, KeyRound, RefreshCw, ArrowRight, Shield, Check, X, MessageSquare, MessageCircle, Users, Link as LinkIcon, ShieldCheck, Mail, Wallet, Clock, Settings as SettingsIcon, IndianRupee, Briefcase, Filter, Database, Package, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/lib/AuthContext";
import { useActiveSchool } from "@/lib/activeSchool";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import AdminMessages from "@/components/poshan/AdminMessages";
import SetupReviews from "@/components/poshan/SetupReviews";
import ShareSetupLinkDialog from "@/components/poshan/ShareSetupLinkDialog";
import { useSetupProgress } from "@/components/poshan/SetupProgress";
import ReferralShare from "@/components/poshan/ReferralShare";
import AdminInbox from "@/components/poshan/AdminInbox";
import AdminAppSettings from "@/components/poshan/AdminAppSettings";
import AdminRateCards from "@/components/poshan/AdminRateCards";
import AdminCrm from "@/components/poshan/AdminCrm";
import AdminPlans from "@/components/poshan/AdminPlans";
import AdminSubscriptions from "@/components/poshan/AdminSubscriptions";
import AddToCrmButton from "@/components/poshan/AddToCrmButton";
import AdminSchoolMessageDialog from "@/components/poshan/AdminSchoolMessageDialog";
import BackupButton from "@/components/BackupButton";
import MobileWelcomeHero from "@/components/mobile/MobileWelcomeHero";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mirror of backend SCHOOL_CATEGORIES + Register.jsx CATEGORIES so admins
// can retroactively fix a mis-selected class category. Keeping this in
// sync with those two files is a manual step (audited by the auth pytest
// contract tests).
const SCHOOL_CATEGORY_OPTIONS = [
  "BV+Primary",
  "BV+Primary+Upper Primary",
  "Upper Primary (6,7,8)",
  "BV+Primary+Upper Primary+High School",
  "High School (IX-X)",
];

// Reusable filter dropdown. Radix Select forbids "" item values, so we
// map the "no filter" state to a sentinel "__all".
function FilterSelect({ value, onChange, options, placeholder, testId }) {
  const ALL = "__all";
  return (
    <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
      <SelectTrigger className="h-9 w-[10rem] text-xs" data-testid={testId}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All {placeholder.toLowerCase()}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const { setActiveId } = useActiveSchool();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  // iter150-review — Controlled tab. Single valid list, falls back to
  // "schools" when the URL has no / an unknown ?tab=. Keeps the URL
  // and the open tab in sync both ways (deep-linking + Android back
  // button). `payments` was dead and has been removed.
  const VALID_TABS = ["schools", "inbox", "messages", "reviews", "rateCards", "plans", "subscriptions", "crm", "settings"];
  const rawTab = params.get("tab") || "";
  const tab = VALID_TABS.includes(rawTab) ? rawTab : "schools";
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  // Iter67: advanced filters surfaced next to the search input. Each
  // value either "" (= no filter) or a discrete enum, so we can compose
  // them inside the `filtered` memo cheaply.
  const [filterSetup, setFilterSetup] = useState("");        // "self" | "admin" | "none"
  const [filterVerified, setFilterVerified] = useState("");  // "yes" | "no"
  const [filterActivation, setFilterActivation] = useState(""); // "active-paid" | "active-free" | "expired" | "never"
  const [filterCategory, setFilterCategory] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  // iter114 — filter by setup completion stage (0/4 .. 4/4).
  const [filterSetupStage, setFilterSetupStage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState({}); // user.id → bool
  const [resetTarget, setResetTarget] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [shareTarget, setShareTarget] = useState(null);
  // School the admin has chosen to message via inbox or WhatsApp (per-row CTA).
  const [msgTarget, setMsgTarget] = useState(null);
  // Manual activation-window dialog. Lets admin set/extend any school user's
  // activeUntil + activePlan without going through payment_orders.
  const [activationTarget, setActivationTarget] = useState(null);
  const [activationDays, setActivationDays] = useState("");
  const [activationPlan, setActivationPlan] = useState("admin_extended");
  // iter138 — manually-paid activation. Admin records offline payment
  // details (cash / UPI / bank transfer) so the audit row in
  // payment_orders captures who paid and how.
  const [activationPaymentMode, setActivationPaymentMode] = useState("");
  const [activationPaymentNote, setActivationPaymentNote] = useState("");
  const [activationAmount, setActivationAmount] = useState("");
  const [plansCatalog, setPlansCatalog] = useState([]);
  // iter145 — inline Category dropdown. `savingCategoryFor` holds the
  // user id whose row is mid-flight so we can dim the row's Select and
  // block double-submits.
  const [savingCategoryFor, setSavingCategoryFor] = useState("");
  const updateCategory = async (u, nextCategory) => {
    if (!nextCategory || nextCategory === u.schoolCategory) return;
    setSavingCategoryFor(u.id);
    try {
      await axios.post(`${API}/admin/users/${u.id}/set-category`, { schoolCategory: nextCategory });
      // Optimistic local patch so the row updates without a full refresh.
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, schoolCategory: nextCategory } : x)));
      toast.success(`Category updated to "${nextCategory}" for ${u.schoolName || u.udiseCode}.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingCategoryFor("");
    }
  };
  const { Cell: ProgressCell, DetailDialog: ProgressDialog, refresh: refreshProgress, byUdise: progressByUdise } = useSetupProgress();
  // iter108 — Payments tab removed (all payment flows deleted). Stub so
  // the unread-badge JSX still resolves to 0 without crashing.
  const pendingPaymentCount = 0;

  const refresh = async (filter = "") => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/users`, { params: filter ? { q: filter } : {} });
      setUsers(r.data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* on mount */ }, []);

  // iter138 — pull plan catalog once so the manual-activation dialog
  // can offer a real Plan picker (with auto-filled validity days).
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/subscription-plans`);
        setPlansCatalog(Array.isArray(r.data?.plans) ? r.data.plans : []);
      } catch (_) { /* non-fatal — dialog falls back to custom days */ }
    })();
  }, []);

  // Distinct categories for the dropdown — collected from the user list.
  const categoryOptions = useMemo(() => {
    const set = new Set();
    for (const u of users) if (u.schoolCategory) set.add(u.schoolCategory);
    return Array.from(set).sort();
  }, [users]);

  // Local search-as-you-type + advanced filter pipeline.
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const now = Date.now();
    return users.filter((u) => {
      if (ql) {
        const hit = (u.udiseCode || "").toLowerCase().includes(ql)
          || (u.schoolName || "").toLowerCase().includes(ql)
          || (u.schoolCategory || "").toLowerCase().includes(ql)
          || (u.mobile || "").toLowerCase().includes(ql);
        if (!hit) return false;
      }
      if (filterSetup) {
        // "none" = field absent. Allow "self" / "admin" exact match.
        if (filterSetup === "none" ? !!u.setupChoice : u.setupChoice !== filterSetup) return false;
      }
      if (filterVerified === "yes" && !u.verified) return false;
      if (filterVerified === "no" && u.verified) return false;
      if (filterActivation) {
        const isFree = (u.activePlan || "").startsWith("free_");
        const expiresMs = u.activeUntil ? new Date(u.activeUntil).getTime() : 0;
        const isExpired = expiresMs > 0 && expiresMs <= now;
        const isActive = expiresMs > now;
        if (filterActivation === "active-paid" && !(isActive && !isFree)) return false;
        if (filterActivation === "active-free" && !(isActive && isFree)) return false;
        if (filterActivation === "expired" && !isExpired) return false;
        if (filterActivation === "never" && !!u.activeUntil) return false;
      }
      if (filterCategory && (u.schoolCategory || "") !== filterCategory) return false;
      if (filterSetupStage !== "") {
        // Match against the visible ProgressCell stage (done/4) — same source the
        // admin sees in the table. Falls back to backend setupStage when
        // setup-progress hasn't loaded yet.
        const progress = progressByUdise?.[u.udiseCode];
        const visibleStage = (typeof progress?.done === "number")
          ? progress.done
          : (typeof u.setupStage === "number" ? u.setupStage : -1);
        if (String(visibleStage) !== filterSetupStage) return false;
      }
      if (filterDateFrom) {
        const t = u.createdAt ? new Date(u.createdAt).getTime() : 0;
        if (t < new Date(filterDateFrom + "T00:00:00").getTime()) return false;
      }
      if (filterDateTo) {
        const t = u.createdAt ? new Date(u.createdAt).getTime() : 0;
        if (t > new Date(filterDateTo + "T23:59:59").getTime()) return false;
      }
      return true;
    });
  }, [users, q, filterSetup, filterVerified, filterActivation, filterCategory, filterSetupStage, filterDateFrom, filterDateTo, progressByUdise]);

  const activeFilterCount = (filterSetup ? 1 : 0) + (filterVerified ? 1 : 0)
    + (filterActivation ? 1 : 0) + (filterCategory ? 1 : 0)
    + (filterSetupStage !== "" ? 1 : 0)
    + (filterDateFrom ? 1 : 0) + (filterDateTo ? 1 : 0);

  const clearFilters = () => {
    setFilterSetup(""); setFilterVerified(""); setFilterActivation("");
    setFilterCategory(""); setFilterSetupStage("");
    setFilterDateFrom(""); setFilterDateTo("");
  };

  const togglePw = (id) => setShowPw((s) => ({ ...s, [id]: !s[id] }));

  const switchToSchool = (u) => {
    if (!u?.schoolId) { toast.error("School profile missing"); return; }
    setActiveId(u.schoolId);
    toast.success(`Now viewing ${u.schoolName}`);
    nav("/dashboard");
  };

  const deleteUser = async (u) => {
    if (!confirm(`Delete ${u.schoolName} (UDISE ${u.udiseCode})? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${u.id}`);
      toast.success("School user removed");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const submitReset = async () => {
    if (!resetTarget || newPw.length < 4) { toast.error("Password must be 4+ chars"); return; }
    try {
      await axios.post(`${API}/admin/users/${resetTarget.id}/reset-password`, { password: newPw });
      toast.success("Password reset");
      setResetTarget(null); setNewPw("");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const openActivationDialog = (u) => {
    setActivationTarget(u);
    // Pre-populate from existing window if any, otherwise default to 30 days.
    if (u.activeUntil) {
      const remain = Math.max(1, Math.ceil((new Date(u.activeUntil).getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
      setActivationDays(String(remain));
    } else {
      setActivationDays("30");
    }
    setActivationPlan(u.activePlan || "admin_extended");
    setActivationPaymentMode("");
    setActivationPaymentNote("");
    setActivationAmount("");
  };

  // iter138 — selecting a real plan auto-fills validity days + rupee amount
  // so the admin doesn't have to re-key them.
  const onActivationPlanChange = (planKey) => {
    setActivationPlan(planKey);
    const p = plansCatalog.find((x) => x.key === planKey);
    if (p) {
      if (p.validity_days && Number(p.validity_days) > 0) {
        setActivationDays(String(p.validity_days));
      }
      if (p.amount_rupees) setActivationAmount(String(p.amount_rupees));
    }
  };

  const submitActivation = async () => {
    if (!activationTarget) return;
    const days = parseInt(activationDays, 10);
    if (!Number.isFinite(days) || days < 1) { toast.error("Days must be a positive integer"); return; }
    try {
      const payload = {
        days,
        plan: activationPlan.trim() || "admin_extended",
        verify: true,
      };
      if (activationPaymentMode) payload.paymentMode = activationPaymentMode;
      if (activationPaymentNote) payload.paymentNote = activationPaymentNote.trim();
      const amt = parseInt(activationAmount, 10);
      if (Number.isFinite(amt) && amt > 0) payload.amountPaid = amt;
      await axios.post(`${API}/admin/users/${activationTarget.id}/set-activation`, payload);
      toast.success(`${activationTarget.schoolName} active for ${days} day${days === 1 ? "" : "s"}`);
      setActivationTarget(null);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const clearActivation = async (u) => {
    if (!confirm(`Revoke active access for ${u.schoolName}? Their dashboard will lock immediately.`)) return;
    try {
      await axios.post(`${API}/admin/users/${u.id}/clear-activation`);
      toast.success("Activation cleared");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const toggleVerify = async (u) => {
    const isVerifying = !u.verified;
    const url = u.verified
      ? `${API}/admin/users/${u.id}/unverify`
      : `${API}/admin/users/${u.id}/verify`;
    try {
      await axios.post(url, {});
      toast.success(u.verified ? `${u.schoolName} unverified` : `${u.schoolName} verified — features unlocked`);
      // Auto-open WhatsApp with confirmation to the school's saved mobile so
      // the admin can send a 1-tap "you're active" notification. Only fires
      // on verify (not unverify) and only when a mobile is on record.
      if (isVerifying && u.mobile && /^\d{10}$/.test(u.mobile)) {
        const text = encodeURIComponent(
          `Namaste ${u.schoolName},\n\n` +
          `Your MDM Sahayak account is now active. ✅\n\n` +
          `UDISE: ${u.udiseCode}\n` +
          `Category: ${u.schoolCategory}\n\n` +
          `You can now log in and use Daily Entry, Fund Management, Quarterly Reports and all other features.`,
        );
        // Indian numbers: prepend country code 91.
        const wa = `https://wa.me/91${u.mobile}?text=${text}`;
        window.open(wa, "_blank", "noopener,noreferrer");
      } else if (isVerifying && !u.mobile) {
        toast.info("No mobile on record — could not auto-send WhatsApp confirmation.");
      }
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <main className="min-h-screen overflow-x-hidden bg-sand-50 text-ink-900 md:overflow-x-visible">
      <Navbar />
      <div className="mx-auto max-w-7xl overflow-x-hidden px-4 py-8 sm:px-6 md:overflow-x-visible lg:px-8">
        {/* iter137 — Phase 3 admin mobile hero (md:hidden), matching the
            school dashboard treatment. */}
        <MobileWelcomeHero
          schoolName="Admin Console"
          subtitle={`Signed in as ${user?.udiseCode || user?.mobile || "—"}`}
          accent="ADMIN"
        />
        <header className="mb-6 hidden flex-wrap items-end justify-between gap-3 md:flex">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-ink-700 flex items-center gap-1"><Shield className="h-3 w-3" /> Admin</p>
            <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">Registered Schools</h1>
            <p className="mt-2 text-sm text-ink-700">Signed in as <b>{user?.udiseCode}</b>. View / edit / open any school's reports.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <BackupButton />
            <Button variant="outline" onClick={() => nav("/admin/field-config")} data-testid="admin-field-config-btn" className="border-amber-300 text-amber-700 hover:bg-amber-50">
              <SettingsIcon className="mr-1 h-4 w-4" /> Field Config
            </Button>
            <Button variant="outline" onClick={() => nav("/admin/nudges")} data-testid="admin-nudges-btn" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
              <MessageCircle className="mr-1 h-4 w-4" /> WhatsApp Nudges
            </Button>
            <Button variant="outline" onClick={() => nav("/admin/migration")} data-testid="admin-migration-export-btn" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
              <Database className="mr-1 h-4 w-4" /> Migration Export
            </Button>
            <Button variant="outline" onClick={() => nav("/admin/fund-check")} data-testid="admin-fund-check-btn" className="border-clay/40 text-clay hover:bg-clay/10">
              Opening Balance Check
            </Button>
            <Button variant="outline" onClick={() => nav("/admin/partner-grants")} data-testid="admin-partner-grants-btn" className="border-clay/40 text-clay hover:bg-clay/10">
              Partner Activations
            </Button>
            <Button variant="outline" onClick={() => { refresh(); refreshProgress(); }} data-testid="admin-refresh-btn"><RefreshCw className="mr-1 h-4 w-4" /> Refresh</Button>
          </div>
        </header>

        <Tabs value={tab} onValueChange={(v) => setParams(v === "schools" ? {} : { tab: v }, { replace: true })} className="space-y-4">
          {/* iter150 — Horizontally scrollable tab strip on mobile so no
              single tab overflows the viewport. `-mx-4 px-4` extends the
              scroll area to the screen edges. The TabsList itself keeps
              its grid-ish behaviour on desktop. */}
          <div className="-mx-4 overflow-x-auto px-4 md:mx-0 md:overflow-visible md:px-0" data-testid="admin-tabs-scroll">
          <TabsList className="inline-flex min-w-max gap-1 bg-sand-100 p-1 md:w-full">
            <TabsTrigger value="schools" data-testid="admin-tab-schools" className="data-[state=active]:bg-clay data-[state=active]:text-white">
              <Users className="mr-1.5 h-3.5 w-3.5" /> Schools
            </TabsTrigger>
            <TabsTrigger value="inbox" data-testid="admin-tab-inbox" className="data-[state=active]:bg-clay data-[state=active]:text-white">
              <Mail className="mr-1.5 h-3.5 w-3.5" /> Inbox
            </TabsTrigger>
            <TabsTrigger value="messages" data-testid="admin-tab-messages" className="data-[state=active]:bg-clay data-[state=active]:text-white">
              <MessageSquare className="mr-1.5 h-3.5 w-3.5" /> Submissions
            </TabsTrigger>
            <TabsTrigger value="reviews" data-testid="admin-tab-reviews" className="data-[state=active]:bg-clay data-[state=active]:text-white">
              <ShieldCheck className="mr-1.5 h-3.5 w-3.5" /> Setup Reviews
            </TabsTrigger>
            {/* iter108 — Payments tab removed (all payment flows deleted) */}
            <TabsTrigger value="rateCards" data-testid="admin-tab-rate-cards" className="data-[state=active]:bg-clay data-[state=active]:text-white">
              <IndianRupee className="mr-1.5 h-3.5 w-3.5" /> Rate Cards
            </TabsTrigger>
            <TabsTrigger value="plans" data-testid="admin-tab-plans" className="data-[state=active]:bg-clay data-[state=active]:text-white">
              <Package className="mr-1.5 h-3.5 w-3.5" /> Plans
            </TabsTrigger>
            <TabsTrigger value="subscriptions" data-testid="admin-tab-subscriptions" className="data-[state=active]:bg-clay data-[state=active]:text-white">
              <CreditCard className="mr-1.5 h-3.5 w-3.5" /> Subscriptions
            </TabsTrigger>
            <TabsTrigger value="crm" data-testid="admin-tab-crm" className="data-[state=active]:bg-clay data-[state=active]:text-white">
              <Briefcase className="mr-1.5 h-3.5 w-3.5" /> CRM
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings" className="data-[state=active]:bg-clay data-[state=active]:text-white">
              <SettingsIcon className="mr-1.5 h-3.5 w-3.5" /> Settings
            </TabsTrigger>
          </TabsList>
          </div>

          <TabsContent value="schools" className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 max-w-md min-w-[12rem]">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-700" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by UDISE / school name / category / mobile"
                  className="pl-9"
                  data-testid="admin-search-input"
                />
              </div>

              {/* Iter67: advanced filters — Setup Type / Verified / Activation / Category / Created date range */}
              <FilterSelect
                value={filterSetup}
                onChange={setFilterSetup}
                testId="admin-filter-setup"
                placeholder="Setup type"
                options={[
                  { value: "self", label: "Self setup" },
                  { value: "admin", label: "Admin/WhatsApp" },
                  { value: "none", label: "Not picked" },
                ]}
              />
              <FilterSelect
                value={filterVerified}
                onChange={setFilterVerified}
                testId="admin-filter-verified"
                placeholder="Verification"
                options={[
                  { value: "yes", label: "Verified" },
                  { value: "no", label: "Pending" },
                ]}
              />
              <FilterSelect
                value={filterActivation}
                onChange={setFilterActivation}
                testId="admin-filter-activation"
                placeholder="Activation"
                options={[
                  { value: "active-paid", label: "Active (paid)" },
                  { value: "active-free", label: "Active (free)" },
                  { value: "expired", label: "Expired" },
                  { value: "never", label: "Never activated" },
                ]}
              />
              <FilterSelect
                value={filterCategory}
                onChange={setFilterCategory}
                testId="admin-filter-category"
                placeholder="Category"
                options={categoryOptions.map((c) => ({ value: c, label: c }))}
              />
              {/* iter114 — setup-stage filter (0/4 … 4/4) */}
              <FilterSelect
                value={filterSetupStage}
                onChange={setFilterSetupStage}
                testId="admin-filter-setup-stage"
                placeholder="Setup stage"
                options={[
                  { value: "4", label: "4/4 Complete" },
                  { value: "3", label: "3/4 Almost done" },
                  { value: "2", label: "2/4 Halfway" },
                  { value: "1", label: "1/4 Just started" },
                  { value: "0", label: "0/4 Empty" },
                ]}
              />
              <Input
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                className="h-9 w-[8.25rem] text-xs"
                title="Created on or after"
                data-testid="admin-filter-date-from"
              />
              <Input
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                className="h-9 w-[8.25rem] text-xs"
                title="Created on or before"
                data-testid="admin-filter-date-to"
              />
              {activeFilterCount > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={clearFilters}
                  className="h-9 gap-1 text-xs"
                  data-testid="admin-filters-clear"
                  title="Clear all advanced filters"
                >
                  <X className="h-3.5 w-3.5" /> Clear ({activeFilterCount})
                </Button>
              )}
              <span className="ml-auto text-xs text-ink-700" data-testid="admin-schools-count">
                {filtered.length} school{filtered.length === 1 ? "" : "s"}
                {activeFilterCount > 0 && (
                  <span className="ml-1 text-ink-700/70">
                    · <Filter className="inline h-3 w-3" /> {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"}
                  </span>
                )}
              </span>
            </div>

        {/* iter148 — Desktop table wrapper (hidden on mobile, mobile card list
            renders below). */}
        <div className="hidden overflow-x-auto rounded-2xl border border-border bg-white shadow-sm md:block">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead className="bg-sand-100 text-ink-900">
              <tr>
                <th className="border border-border p-2 text-left">UDISE</th>
                <th className="border border-border p-2 text-left">School</th>
                <th className="border border-border p-2 text-left">Category</th>
                <th className="border border-border p-2 text-left">Mobile</th>
                <th className="border border-border p-2 text-left">Setup</th>
                <th className="border border-border p-2 text-left">Setup</th>
                <th className="border border-border p-2 text-left">Verified</th>
                <th className="border border-border p-2 text-left">Password</th>
                <th className="border border-border p-2 text-left">Created</th>
                <th className="border border-border p-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={10} className="p-6 text-center text-ink-700">Loading…</td></tr>)}
              {!loading && filtered.length === 0 && (<tr><td colSpan={10} className="p-6 text-center text-ink-700">No registered schools yet.</td></tr>)}
              {filtered.map((u) => (
                <tr key={u.id} className="border-t border-border hover:bg-sand-50" data-testid={`admin-user-row-${u.udiseCode}`}>
                  <td className="border border-border p-2 font-mono" data-private>{u.udiseCode}</td>
                  <td className="border border-border p-2 font-semibold">{u.schoolName}</td>
                  <td className="border border-border p-2">
                    <Select
                      value={u.schoolCategory || ""}
                      onValueChange={(v) => updateCategory(u, v)}
                      disabled={savingCategoryFor === u.id}
                    >
                      <SelectTrigger
                        className="h-8 min-w-[8.5rem] max-w-[12rem] px-2 py-1 text-xs"
                        data-testid={`admin-category-trigger-${u.udiseCode}`}
                      >
                        <SelectValue placeholder="Set category" />
                      </SelectTrigger>
                      <SelectContent>
                        {SCHOOL_CATEGORY_OPTIONS.map((opt) => (
                          <SelectItem
                            key={opt}
                            value={opt}
                            data-testid={`admin-category-option-${u.udiseCode}-${opt}`}
                          >
                            {opt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="border border-border p-2 font-mono" data-private>{u.mobile || "—"}</td>
                  <td className="border border-border p-2">{u.setupChoice ? (u.setupChoice === "self" ? "Self" : "Admin/WhatsApp") : "—"}</td>
                  <td className="border border-border p-2">
                    <ProgressCell udiseCode={u.udiseCode} />
                  </td>
                  <td className="border border-border p-2">
                    {u.verified ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800" data-testid={`admin-verified-badge-${u.udiseCode}`}>
                        <Check className="h-3 w-3" /> Verified
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                        <X className="h-3 w-3" /> Pending
                      </span>
                    )}
                    {u.activeUntil && (() => {
                      const expires = new Date(u.activeUntil);
                      const ms = expires.getTime() - Date.now();
                      const days = Math.ceil(ms / (1000 * 60 * 60 * 24));
                      const expired = ms <= 0;
                      const isFree = (u.activePlan || "").startsWith("free_");
                      const label = expired
                        ? `Expired ${expires.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}`
                        : `${days}d left · ${expires.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}`;
                      return (
                        <p
                          className={`mt-1 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[0.62rem] font-semibold uppercase tracking-[0.1em] ${
                            expired
                              ? "bg-red-100 text-red-700"
                              : isFree
                              ? "bg-clay/15 text-clay-dark"
                              : "bg-moss/15 text-moss-dark"
                          }`}
                          data-testid={`admin-activation-state-${u.udiseCode}`}
                        >
                          {isFree ? "🎁" : "⭐"} {label}
                        </p>
                      );
                    })()}
                    <AdminPlanInfo u={u} />
                  </td>
                  <td className="border border-border p-2 font-mono">
                    <span className="mr-2">{showPw[u.id] ? (u.passwordPlain || "—") : "••••••••"}</span>
                    <button onClick={() => togglePw(u.id)} className="text-clay hover:underline" data-testid={`admin-toggle-pw-${u.udiseCode}`}>
                      {showPw[u.id] ? <EyeOff className="inline h-3.5 w-3.5" /> : <Eye className="inline h-3.5 w-3.5" />}
                    </button>
                  </td>
                  <td className="border border-border p-2 text-xs text-ink-700">{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : "—"}</td>
                  <td className="border border-border p-2">
                    <div className="flex flex-wrap items-center justify-center gap-1">
                      <Button
                        size="sm"
                        variant={u.verified ? "ghost" : "default"}
                        onClick={() => toggleVerify(u)}
                        className={u.verified ? "text-amber-700 hover:text-amber-800" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
                        data-testid={`admin-toggle-verify-${u.udiseCode}`}
                        title={u.verified ? "Revoke verification" : "Verify school"}
                      >
                        {u.verified ? <X className="h-3.5 w-3.5" /> : <><Check className="mr-1 h-3.5 w-3.5" /> Verify</>}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openActivationDialog(u)}
                        title={u.activeUntil ? `Active until ${new Date(u.activeUntil).toLocaleDateString("en-IN")} · click to extend or activate paid plan` : "Activate paid plan / set free window"}
                        data-testid={`admin-set-activation-${u.udiseCode}`}
                        className={u.activeUntil && new Date(u.activeUntil) > new Date() ? "text-moss-dark hover:text-moss-dark/80" : "text-clay hover:text-clay-dark"}
                      >
                        <CreditCard className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => switchToSchool(u)} title="Open this school's dashboard" data-testid={`admin-open-${u.udiseCode}`}>
                        <ArrowRight className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setMsgTarget(u)} title="Send message via web inbox or WhatsApp" data-testid={`admin-msg-${u.udiseCode}`} className="text-clay hover:text-clay-dark">
                        <MessageSquare className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setShareTarget(u)} title="Share setup link via WhatsApp" data-testid={`admin-share-link-${u.udiseCode}`} className="text-clay hover:text-clay-dark">
                        <LinkIcon className="h-3.5 w-3.5" />
                      </Button>
                      <AddToCrmButton
                        variant="icon"
                        testIdSuffix={u.udiseCode}
                        schoolName={u.schoolName}
                        mobile={u.mobile}
                        udiseCode={u.udiseCode}
                        district={u.district || ""}
                        block={u.block || ""}
                        notePreview={`School row · ${u.schoolCategory || ""} · setupChoice ${u.setupChoice || "—"} · verified ${u.verified ? "yes" : "no"}`}
                      />
                      <Button size="sm" variant="ghost" onClick={() => { setResetTarget(u); setNewPw(""); }} title="Reset password" data-testid={`admin-reset-${u.udiseCode}`}>
                        <KeyRound className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteUser(u)} className="text-red-600 hover:text-red-700" title="Delete" data-testid={`admin-delete-${u.udiseCode}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* iter148 — Mobile card list. Mirrors the desktop table but fits
            a phone screen. Shows the essentials: school identity, mobile,
            category (inline Select), setup progress, verified/activation
            badge, and the most common actions. Rare admin icons collapse
            into a "⋯ More" expander. */}
        <div className="space-y-3 md:hidden" data-testid="admin-users-cards">
          {loading && (
            <p className="rounded-2xl border border-border bg-white p-5 text-center text-sm text-ink-700">Loading…</p>
          )}
          {!loading && filtered.length === 0 && (
            <p className="rounded-2xl border border-border bg-white p-5 text-center text-sm text-ink-700">No registered schools yet.</p>
          )}
          {filtered.map((u) => {
            const expires = u.activeUntil ? new Date(u.activeUntil) : null;
            const expired = expires ? expires.getTime() <= Date.now() : false;
            const daysLeft = expires ? Math.ceil((expires.getTime() - Date.now()) / 86400000) : null;
            const isFree = (u.activePlan || "").startsWith("free_");
            return (
              <article
                key={u.id}
                data-testid={`admin-user-card-${u.udiseCode}`}
                className="rounded-2xl border border-border bg-white p-4 shadow-sm"
              >
                <header className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold text-ink-900">{u.schoolName || "—"}</p>
                    <p className="mt-0.5 font-mono text-[0.7rem] text-ink-700">UDISE <span data-private>{u.udiseCode}</span></p>
                  </div>
                  {u.verified ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[0.65rem] font-semibold text-emerald-800">
                      <Check className="h-3 w-3" /> Verified
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[0.65rem] font-semibold text-amber-800">
                      <X className="h-3 w-3" /> Pending
                    </span>
                  )}
                </header>
                <AdminPlanInfo u={u} />

                <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-[0.72rem]">
                  <div>
                    <dt className="font-semibold uppercase tracking-[0.1em] text-ink-500">Mobile</dt>
                    <dd className="mt-0.5 font-mono text-ink-900" data-private>{u.mobile || "—"}</dd>
                  </div>
                  <div>
                    <dt className="font-semibold uppercase tracking-[0.1em] text-ink-500">Setup type</dt>
                    <dd className="mt-0.5 text-ink-900">{u.setupChoice ? (u.setupChoice === "self" ? "Self" : "Admin") : "—"}</dd>
                  </div>
                  <div>
                    <dt className="font-semibold uppercase tracking-[0.1em] text-ink-500">Progress</dt>
                    <dd className="mt-0.5 text-ink-900"><ProgressCell udiseCode={u.udiseCode} /></dd>
                  </div>
                  <div>
                    <dt className="font-semibold uppercase tracking-[0.1em] text-ink-500">Created</dt>
                    <dd className="mt-0.5 text-ink-900">{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : "—"}</dd>
                  </div>
                </dl>

                <div className="mt-3">
                  <p className="mb-1 text-[0.65rem] font-semibold uppercase tracking-[0.1em] text-ink-500">Category</p>
                  <Select
                    value={u.schoolCategory || ""}
                    onValueChange={(v) => updateCategory(u, v)}
                    disabled={savingCategoryFor === u.id}
                  >
                    <SelectTrigger className="h-9 text-xs" data-testid={`admin-category-trigger-m-${u.udiseCode}`}>
                      <SelectValue placeholder="Set category" />
                    </SelectTrigger>
                    <SelectContent>
                      {SCHOOL_CATEGORY_OPTIONS.map((opt) => (
                        <SelectItem key={opt} value={opt} data-testid={`admin-category-option-m-${u.udiseCode}-${opt}`}>
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {expires && (
                  <p
                    className={`mt-3 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.62rem] font-semibold uppercase tracking-[0.1em] ${
                      expired
                        ? "bg-red-100 text-red-700"
                        : isFree
                          ? "bg-clay/15 text-clay-dark"
                          : "bg-moss/15 text-moss-dark"
                    }`}
                    data-testid={`admin-activation-state-m-${u.udiseCode}`}
                  >
                    {isFree ? "🎁" : "⭐"}
                    {expired
                      ? ` Expired ${expires.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}`
                      : ` ${daysLeft}d left · ${expires.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}`}
                  </p>
                )}

                <div className="mt-4 flex flex-wrap gap-2 border-t border-border pt-3">
                  <Button
                    size="sm"
                    variant={u.verified ? "ghost" : "default"}
                    onClick={() => toggleVerify(u)}
                    className={u.verified ? "flex-1 text-amber-700" : "flex-1 bg-emerald-600 text-white hover:bg-emerald-700"}
                    data-testid={`admin-toggle-verify-m-${u.udiseCode}`}
                  >
                    {u.verified ? (<><X className="mr-1 h-3.5 w-3.5" /> Unverify</>) : (<><Check className="mr-1 h-3.5 w-3.5" /> Verify</>)}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openActivationDialog(u)}
                    className="flex-1 gap-1"
                    data-testid={`admin-set-activation-m-${u.udiseCode}`}
                  >
                    <CreditCard className="h-3.5 w-3.5" /> Plan
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => switchToSchool(u)}
                    className="flex-1 gap-1"
                    data-testid={`admin-open-m-${u.udiseCode}`}
                  >
                    <ArrowRight className="h-3.5 w-3.5" /> Open
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setMsgTarget(u)}
                    className="text-clay"
                    data-testid={`admin-msg-m-${u.udiseCode}`}
                    aria-label="Message school"
                  >
                    <MessageSquare className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShareTarget(u)}
                    className="text-clay"
                    data-testid={`admin-share-link-m-${u.udiseCode}`}
                    aria-label="Share setup link"
                  >
                    <LinkIcon className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setResetTarget(u); setNewPw(""); }}
                    data-testid={`admin-reset-m-${u.udiseCode}`}
                    aria-label="Reset password"
                  >
                    <KeyRound className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => deleteUser(u)}
                    className="text-red-600 hover:text-red-700"
                    data-testid={`admin-delete-m-${u.udiseCode}`}
                    aria-label="Delete school"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </article>
            );
          })}
        </div>
          </TabsContent>

          <TabsContent value="messages">
            <AdminMessages />
          </TabsContent>

          <TabsContent value="inbox">
            <AdminInbox />
          </TabsContent>

          <TabsContent value="reviews">
            <SetupReviews />
          </TabsContent>

          {/* iter108 — Payments tab removed (all payment flows deleted) */}

          <TabsContent value="rateCards">
            <AdminRateCards />
          </TabsContent>

          <TabsContent value="plans">
            <AdminPlans />
          </TabsContent>

          <TabsContent value="subscriptions">
            <AdminSubscriptions />
          </TabsContent>

          <TabsContent value="crm">
            <AdminCrm />
          </TabsContent>

          <TabsContent value="settings">
            <AdminAppSettings />
          </TabsContent>
        </Tabs>

        {/* Refer & Share — admin can also share the platform with peers. */}
        <section className="mt-8">
          <ReferralShare />
        </section>

        <ShareSetupLinkDialog
          open={!!shareTarget}
          onOpenChange={(o) => !o && setShareTarget(null)}
          school={shareTarget}
        />

        <AdminSchoolMessageDialog
          open={!!msgTarget}
          school={msgTarget}
          onClose={() => setMsgTarget(null)}
        />

        <ProgressDialog />

        <Dialog open={!!resetTarget} onOpenChange={(o) => !o && setResetTarget(null)}>
          <DialogContent data-testid="admin-reset-dialog">
            <DialogHeader>
              <DialogTitle>Reset password — {resetTarget?.schoolName}</DialogTitle>
              <DialogDescription>UDISE {resetTarget?.udiseCode} · sets a new password (admin sees both).</DialogDescription>
            </DialogHeader>
            <Input value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="New password (4+ chars)" data-testid="admin-reset-password-input" />
            <DialogFooter>
              <Button variant="ghost" onClick={() => setResetTarget(null)}>Cancel</Button>
              <Button onClick={submitReset} className="bg-clay hover:bg-clay/90" data-testid="admin-reset-confirm-btn">Reset</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!activationTarget} onOpenChange={(o) => !o && setActivationTarget(null)}>
          <DialogContent data-testid="admin-activation-dialog" className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><CreditCard className="h-5 w-5 text-clay" /> Activate Paid Plan</DialogTitle>
              <DialogDescription>
                {activationTarget?.schoolName} · UDISE {activationTarget?.udiseCode}
                {activationTarget?.activeUntil && (
                  <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-moss/15 px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-[0.1em] text-moss-dark">
                    Currently {new Date(activationTarget.activeUntil) > new Date() ? "active" : "expired"} until {new Date(activationTarget.activeUntil).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              {/* iter138 — plan picker. Selecting auto-fills days + amount. */}
              <label className="block">
                <span className="text-xs font-semibold text-ink-700">Plan</span>
                <Select value={activationPlan} onValueChange={onActivationPlanChange}>
                  <SelectTrigger className="mt-1" data-testid="admin-activation-plan-select">
                    <SelectValue placeholder="Select a plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {plansCatalog
                      .filter((p) => p.key !== "admin_setup")
                      .map((p) => (
                        <SelectItem key={p.key} value={p.key} data-testid={`admin-activation-plan-opt-${p.key}`}>
                          {p.label} · ₹{p.amount_rupees}{p.validity_days ? ` · ${p.validity_days}d` : ""}
                        </SelectItem>
                      ))}
                    <SelectItem value="admin_extended" data-testid="admin-activation-plan-opt-admin_extended">
                      Custom · Admin-extended window
                    </SelectItem>
                  </SelectContent>
                </Select>
                <span className="block mt-1 text-[0.65rem] text-ink-700">
                  Pick a real plan for paid users — days &amp; price auto-fill. Use <code>admin_extended</code> for free extensions / corrections.
                </span>
              </label>

              <label className="block">
                <span className="text-xs font-semibold text-ink-700">Days from now</span>
                <Input
                  type="number"
                  min={1}
                  max={3650}
                  value={activationDays}
                  onChange={(e) => setActivationDays(e.target.value)}
                  placeholder="30"
                  data-testid="admin-activation-days"
                />
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {[7, 30, 90, 365].map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setActivationDays(String(d))}
                      data-testid={`admin-activation-preset-${d}`}
                      className="rounded-full border border-border bg-sand-50 px-2.5 py-1 text-xs font-semibold text-ink-900 hover:bg-clay/10"
                    >
                      {d} days
                    </button>
                  ))}
                </div>
              </label>

              {/* iter138 — offline payment audit fields. Optional but
                  recommended so we can prove activation was paid for. */}
              <div className="rounded-lg border border-border bg-sand-50/60 p-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-ink-700">Manual Payment Details (optional)</p>
                <label className="block">
                  <span className="text-xs font-semibold text-ink-700">Payment Mode</span>
                  <Select value={activationPaymentMode || "__none"} onValueChange={(v) => setActivationPaymentMode(v === "__none" ? "" : v)}>
                    <SelectTrigger className="mt-1" data-testid="admin-activation-paymode-select">
                      <SelectValue placeholder="No record" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">No record (free extension)</SelectItem>
                      <SelectItem value="cash" data-testid="admin-activation-paymode-cash">Cash</SelectItem>
                      <SelectItem value="upi" data-testid="admin-activation-paymode-upi">UPI</SelectItem>
                      <SelectItem value="bank_transfer" data-testid="admin-activation-paymode-bank">Bank Transfer</SelectItem>
                      <SelectItem value="cheque" data-testid="admin-activation-paymode-cheque">Cheque</SelectItem>
                      <SelectItem value="other" data-testid="admin-activation-paymode-other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </label>
                <label className="mt-2 block">
                  <span className="text-xs font-semibold text-ink-700">Amount Paid (₹)</span>
                  <Input
                    type="number"
                    min={0}
                    value={activationAmount}
                    onChange={(e) => setActivationAmount(e.target.value)}
                    placeholder="e.g. 199"
                    data-testid="admin-activation-amount"
                  />
                </label>
                <label className="mt-2 block">
                  <span className="text-xs font-semibold text-ink-700">Reference / Note</span>
                  <Input
                    value={activationPaymentNote}
                    onChange={(e) => setActivationPaymentNote(e.target.value)}
                    placeholder="UTR, cheque #, txn ID, or remarks"
                    maxLength={300}
                    data-testid="admin-activation-note"
                  />
                </label>
              </div>
            </div>
            <DialogFooter className="gap-2">
              {activationTarget?.activeUntil && (
                <Button
                  variant="ghost"
                  onClick={() => { clearActivation(activationTarget); setActivationTarget(null); }}
                  className="text-red-600 hover:text-red-700"
                  data-testid="admin-activation-clear-btn"
                >
                  Revoke
                </Button>
              )}
              <Button variant="outline" onClick={() => setActivationTarget(null)} data-testid="admin-activation-cancel-btn">Cancel</Button>
              <Button onClick={submitActivation} className="bg-clay text-white hover:bg-clay-dark" data-testid="admin-activation-save-btn">
                Activate Plan
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </main>
  );
}
